import imageCompression from "browser-image-compression";
import { readImageMetaFromHeader } from "./imageDimensions";

export const MAX_WIDTH_OR_HEIGHT = 1920;

/** 출력 용량 상한. 기존 경로의 maxSizeMB 와 같은 값이라 경로가 갈려도 결과가 같다. */
export const MAX_OUTPUT_MB = 1;

/** 재인코딩 품질. 기존 경로의 initialQuality 와 같은 값을 쓴다. */
const BASE_QUALITY = 0.7;

/**
 * 1MB 를 넘으면 이 순서로 품질을 낮춰 다시 시도한다. 기존 경로는
 * maxSizeMB 로 용량을 보장했으므로, 새 경로만 보장을 잃으면 같은 업로드
 * 안에서 파일마다 규칙이 달라진다.
 */
const QUALITY_LADDER = [BASE_QUALITY, 0.55, 0.4];

export const COMPRESSION_OPTION = {
  maxSizeMB: MAX_OUTPUT_MB,
  maxWidthOrHeight: MAX_WIDTH_OR_HEIGHT,
  initialQuality: BASE_QUALITY,
};

/**
 * SOF 마커까지 닿기에 넉넉한 창. EXIF 썸네일이 들어가면 앞부분이 수십 KB 까지
 * 커진다. 이 안에서 못 찾으면 null 이 되어 기존 경로로 되돌아갈 뿐이다.
 */
const HEADER_BYTES = 256 * 1024;

/**
 * 업로드용으로 이미지를 줄인다.
 *
 * 왜 디코드 시점에 줄이는가: 원본 크기로 한 번 디코드하면 화소 수만큼 메모리를
 * 잡는다. 8064x6048 사진 한 장이 RGBA 비트맵만으로 186MB 이고, 실측 렌더러
 * 사용량은 538MB 였다. 그 파일은 6.4MB 라 장당 10MB 가드에 걸리지 않는다 —
 * 가드가 재는 값은 바이트인데 실제로 드는 값은 화소라서 둘이 어긋난다.
 * createImageBitmap 의 축소 옵션은 목표 크기로 바로 디코드해 원본 크기 비트맵을
 * 아예 만들지 않는다. 같은 사진을 그렇게 재면 263MB 였다 (VMD-416 실측).
 *
 * 화소 수로 업로드를 거절하지는 않는다. 매장은 자기 폰이 찍는 사진 크기를 고를 수
 * 없으므로, 거절당하면 빠져나갈 길이 없다. 비용을 묶되 입력은 막지 않는다.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  try {
    const resized = await resizeAtDecode(file);
    if (resized) return resized;
  } catch (error) {
    // 축소 디코드를 못 하는 환경이면 기존 경로로 되돌아간다. 비싸지만 결과는 같다.
    // 여기서 던지면 호출부가 업로드 전체를 실패로 처리한다.
    console.warn("decode-time resize unavailable, falling back", error);
  }
  const compressed = await imageCompression(file, COMPRESSION_OPTION);
  return new File([compressed], compressed.name, { type: file.type });
}

async function encodeWithinCap(canvas: HTMLCanvasElement): Promise<Blob | null> {
  const cap = MAX_OUTPUT_MB * 1024 * 1024;
  let last: Blob | null = null;
  for (const quality of QUALITY_LADDER) {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return last;
    last = blob;
    if (blob.size <= cap) return blob;
  }
  // 사다리를 다 내려가도 넘으면 가장 작은 것을 쓴다. 되돌아가는 경로도 같은
  // 상황에서 상한을 못 지키므로, 여기서 실패시키면 오히려 더 나쁘다.
  return last;
}

async function resizeAtDecode(file: File): Promise<File | null> {
  // JPEG 으로만 간다. PNG 를 JPEG 으로 내보내면 투명한 부분이 검게 차고,
  // PNG 로 내보내면 사진에서는 오히려 커진다. 매장 카메라 사진은 JPEG 이다.
  if (file.type !== "image/jpeg") return null;
  if (typeof createImageBitmap !== "function") return null;
  if (typeof document === "undefined") return null;

  const meta = readImageMetaFromHeader(
    await file.slice(0, HEADER_BYTES).arrayBuffer(),
  );
  if (!meta) return null;

  // meta 의 가로 세로는 EXIF 방향을 반영한 값이다. imageOrientation 을
  // from-image 로 주면 브라우저가 회전을 픽셀에 적용하므로, 목표 크기도 회전
  // 뒤 기준이어야 한다. 둘을 어긋나게 주면 세로 사진이 찌그러진다.
  const longest = Math.max(meta.width, meta.height);
  // 이미 목표보다 작으면 다시 인코딩해 봐야 화질만 깎인다.
  if (longest <= MAX_WIDTH_OR_HEIGHT) return null;

  const scale = MAX_WIDTH_OR_HEIGHT / longest;
  const width = Math.max(1, Math.round(meta.width * scale));
  const height = Math.max(1, Math.round(meta.height * scale));

  // 폰 사진은 회전을 EXIF 로만 담는다. 명시하지 않으면 브라우저에 따라 회전이
  // 적용되지 않는데, canvas 로 다시 인코딩하면 EXIF 자체가 사라져 세로 사진이
  // 누운 채로 올라간다. 지금 표준은 from-image 가 기본값이지만 구형 WebKit 은
  // none 이 기본이라, iOS 15 를 받는 이상 명시해야 한다.
  //
  // TypeScript 4.5 의 DOM 타입은 "none" 과 "flipY" 만 알고 "from-image" 를
  // 모른다. 표준이 그 뒤에 넓어진 것이고 런타임에서는 유효하므로, 타입만
  // 여기서 넓혀 준다.
  const bitmap = await createImageBitmap(file, {
    imageOrientation: "from-image",
    resizeWidth: width,
    resizeHeight: height,
    resizeQuality: "high",
  } as unknown as ImageBitmapOptions);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0);
    const blob = await encodeWithinCap(canvas);
    if (!blob) return null;
    return new File([blob], file.name, { type: "image/jpeg" });
  } finally {
    // GC 를 기다리면 다음 장 디코드와 겹친다. 30장까지 고를 수 있으므로
    // 겹치는 순간이 가장 위험하다.
    bitmap.close();
  }
}
